/**
 * Centralized icon mapping — FontAwesome Pro Kit with lucide-compatible API.
 *
 * Kit: @awesome.me/kit-b5f8a1a8d5 (Neeve custom Pro build)
 * Includes proprietary Neeve glyphs (node, system-network, portfolio, etc.)
 */
import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';

// Pro solid icons
import {
  faChartLine,
  faCircleExclamation,
  faTriangleExclamation,
  faArrowRight,
  faBell,
  faBoxesStacked,
  faCheck,
  faCircleCheck,
  faChevronDown,
  faChevronLeft,
  faChevronRight,
  faAnglesLeft,
  faAnglesRight,
  faSort,
  faChevronUp,
  faMicrochip,
  faDownload,
  faArrowUpRightFromSquare,
  faFileArrowDown,
  faFileLines,
  faGlobe,
  faInbox,
  faCircleInfo,
  faKey,
  faEnvelope,
  faTerminal,
  faGrip,
  faLock,
  faRightFromBracket,
  faExpand,
  faBars,
  faMinus,
  faNetworkWired,
  faCube,
  faPlay,
  faArrowsRotate,
  faRotateLeft,
  faMagnifyingGlass,
  faServer,
  faShield,
  faShieldHalved,
  faTrashCan,
  faTrophy,
  faUsers,
  faXmark,
  faCircleXmark,
  faArrowUp,
  faArrowDown,
  faArrowLeft,
  faFilter,
  faHardDrive,
  faGaugeHigh,
  faList,
  faLocationDot,
  faMemory as faMemoryIcon,
  faBoxOpen,
  faGear,
  faShieldVirus,
  faWifi,
  faPlus,
  faCopy,
  faEllipsisVertical,
  faEllipsis,
} from '@awesome.me/kit-b5f8a1a8d5/icons/classic/solid';

// Pro regular icons (outline style)
import {
  faCalendar,
  faClock,
  faEye,
  faEyeSlash,
  faFile,
  faLightbulb,
  faComment,
  faPaperPlane,
} from '@awesome.me/kit-b5f8a1a8d5/icons/classic/regular';

// Custom Neeve kit icons (proprietary glyphs)
import {
  faNode48,
  faSystemNetwork48,
  faPortfolio48,
  faUsers48,
  faEndpoint48,
} from '@awesome.me/kit-b5f8a1a8d5/icons/kit/custom';

// --- Icon component type (replaces LucideIcon) ---
type IconWrapperProps = {
  className?: string;
  onClick?: React.MouseEventHandler;
  'aria-label'?: string;
  'aria-hidden'?: boolean | 'true' | 'false';
  [key: string]: unknown;
};

export type IconComponent = React.ForwardRefExoticComponent<
  IconWrapperProps & React.RefAttributes<SVGSVGElement>
>;

// Refs must reach the underlying <svg> — Radix asChild triggers (Tooltip,
// Popover, etc.) anchor their floating content to the child's ref, and the
// lucide icons this wrapper replaced all forwarded refs.
function wrap(icon: IconDefinition): IconComponent {
  const Wrapped = React.forwardRef<SVGSVGElement, IconWrapperProps>((rawProps, ref) => {
    // PropsWithoutRef collapses index-signature types to unknown — restore the real prop types.
    const { className, ...props } = rawProps as IconWrapperProps;
    return <FontAwesomeIcon ref={ref} icon={icon} className={className} {...props} />;
  });
  Wrapped.displayName = `Icon(${icon.iconName})`;
  return Wrapped;
}

// --- Exports (lucide name → FontAwesome Pro icon) ---

// Navigation & UI
export const ChevronDown = wrap(faChevronDown);
export const ChevronUp = wrap(faChevronUp);
export const ChevronLeft = wrap(faChevronLeft);
export const ChevronRight = wrap(faChevronRight);
export const ChevronsLeft = wrap(faAnglesLeft);
export const ChevronsRight = wrap(faAnglesRight);
export const ChevronsUpDown = wrap(faSort);
export const ArrowRight = wrap(faArrowRight);
export const Menu = wrap(faBars);
export const MoreVertical = wrap(faEllipsisVertical);
export const MoreHorizontal = wrap(faEllipsis);
export const X = wrap(faXmark);
export const Search = wrap(faMagnifyingGlass);
export const ExternalLink = wrap(faArrowUpRightFromSquare);
export const Maximize2 = wrap(faExpand);
export const LayoutGrid = wrap(faGrip);
export const LayoutDashboard = wrap(faGaugeHigh);
export const ArrowUp = wrap(faArrowUp);
export const ArrowDown = wrap(faArrowDown);
export const ArrowLeft = wrap(faArrowLeft);
export const Filter = wrap(faFilter);
export const List = wrap(faList);
export const MapPin = wrap(faLocationDot);
export const Settings = wrap(faGear);

// Status & Alerts
export const Check = wrap(faCheck);
export const CheckCircle = wrap(faCircleCheck);
export const CheckCircle2 = wrap(faCircleCheck);
export const XCircle = wrap(faCircleXmark);
export const AlertCircle = wrap(faCircleExclamation);
export const AlertTriangle = wrap(faTriangleExclamation);
export const Info = wrap(faCircleInfo);
export const Bell = wrap(faBell);

// Actions
export const Download = wrap(faDownload);
export const FileDown = wrap(faFileArrowDown);
export const RefreshCw = wrap(faArrowsRotate);
export const RotateCcw = wrap(faRotateLeft);
export const Play = wrap(faPlay);
export const Send = wrap(faPaperPlane);
export const Plus = wrap(faPlus);
export const Copy = wrap(faCopy);
export const Trash2 = wrap(faTrashCan);
export const LogOut = wrap(faRightFromBracket);
export const Eye = wrap(faEye);
export const EyeOff = wrap(faEyeSlash);
export const Minus = wrap(faMinus);

// Content & Files
export const FileText = wrap(faFileLines);
export const FileSpreadsheet = wrap(faFile);
export const MessageCircle = wrap(faComment);
export const Lightbulb = wrap(faLightbulb);
export const Inbox = wrap(faInbox);

// Infrastructure
export const Server = wrap(faServer);
export const Cpu = wrap(faMicrochip);
export const Network = wrap(faNetworkWired);
export const Globe = wrap(faGlobe);
export const Package = wrap(faCube);
export const Boxes = wrap(faBoxesStacked);
export const HardDrive = wrap(faHardDrive);
export const MemoryStick = wrap(faMemoryIcon);
export const Wifi = wrap(faWifi);
export const WifiOff = wrap(faWifi); // Same icon, styled differently in context
export const PackageCheck = wrap(faBoxOpen);

// Security
export const Shield = wrap(faShield);
export const ShieldAlert = wrap(faShieldHalved);
export const ShieldCheck = wrap(faShieldVirus);
export const Lock = wrap(faLock);
export const Key = wrap(faKey);
export const Mail = wrap(faEnvelope);
export const Monitor = wrap(faTerminal);

// Data & Time
export const Calendar = wrap(faCalendar);
export const Clock = wrap(faClock);
export const Activity = wrap(faChartLine);
export const Trophy = wrap(faTrophy);
export const Users = wrap(faUsers);

// --- Neeve custom kit icons ---
export const NeeveNode = wrap(faNode48);
export const NeeveSystemNetwork = wrap(faSystemNetwork48);
export const NeevePortfolio = wrap(faPortfolio48);
export const NeeveUsers = wrap(faUsers48);
export const NeeveEndpoint = wrap(faEndpoint48);
